import axios from "axios";

import { env } from "@/config/env";
import {
  API_ERROR_CODES,
  isJwtAuthErrorCode,
  type ApiErrorBody,
} from "@/lib/api/api-error";
import { redirectToLoginHashRoute } from "@/lib/api/auth-login-redirect";
import { clearAuthSession, getAccessToken } from "@/lib/auth/session";

const PUBLIC_AUTH_API_PATHS = [
  env.authLoginPath,
  env.userRegisterPath,
  env.authForgotPasswordPath,
  env.authResetPasswordPath,
  env.authVerifyEmailPath,
  env.authResendVerificationPath,
] as const;

export function isPublicAuthApiRequest(url: string | undefined): boolean {
  if (!url) {
    return false;
  }

  return PUBLIC_AUTH_API_PATHS.some((path) => url.includes(path));
}

export function shouldHandleJwtAuthFailure(
  status: number | undefined,
  code: string | undefined,
  requestUrl: string | undefined,
): boolean {
  if (status !== 401 || !isJwtAuthErrorCode(code)) {
    return false;
  }

  if (code === API_ERROR_CODES.jwtAuthenticationRequired && !getAccessToken()) {
    return false;
  }

  return !isPublicAuthApiRequest(requestUrl);
}

const apiClient = axios.create({
  baseURL: env.apiUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

apiClient.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const data = error.response?.data as ApiErrorBody | undefined;
      const code = data?.code;
      const requestUrl = error.config?.url;

      if (shouldHandleJwtAuthFailure(status, code, requestUrl)) {
        clearAuthSession();
        redirectToLoginHashRoute();
      }
    }

    return Promise.reject(error);
  },
);

export default apiClient;
